import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import Modal from 'react-modal';

import logout from '../../js/adobe-primetime/logout';
import { MINUTE_IN_MILLISECONDS } from '../../js/utils/intervals';

import settings from './settings';

import getPlayerMount from './getPlayerMount';
import LockScreen from './LockScreen';
import FreePreview from './FreePreview';

/**
 * Convert milliseconds to a formatted timestamp for video player.
 *
 * @param millis integer Milliseconds remaining.
 * @returns {string} Formatted timestamp.
 */
const secondsToTimestamp = (millis) => {
  if (! Number.isInteger(millis)) {
    return '0:00';
  }
  const minutes = Math.floor(millis / MINUTE_IN_MILLISECONDS);
  const seconds = ((millis % MINUTE_IN_MILLISECONDS) / 1000).toFixed(0);
  return `${minutes}:${(10 > seconds ? '0' : '') + seconds}`;
};

// eslint-disable-next-line max-len
const previewDuration = MINUTE_IN_MILLISECONDS * settings('options.preview_duration');

/**
 * Styles for the overlay background.
 *
 * @type {{backgroundImage: string}}
 */
const overlayStyles = settings('options.lock_screen_image')
  ? {
    // eslint-disable-next-line max-len
    backgroundImage: `linear-gradient(rgba(0,0,0,.44),rgba(0,0,0,.44)),url(${settings(
      'options.lock_screen_image',
    )})`,
  }
  : {};

/**
 * Callback returning a node to which the player overlay will be mounted.
 *
 * @type {function(): Element}
 */
const playerMount = () => getPlayerMount();

const PlayerOverlay = ({
  authenticated,
  authorized,
  isAuthCompleted,
  previewRemaining,
  onConnectClick,
  errorMessage = '',
}) => {
  if (! playerMount()) {
    // eslint-disable-next-line no-console
    console.warn('Primetime lockscreen was unable to find a player!');
    return null;
  }

  // Keep the overlay open if unauthenticated or unauthorized.
  const [isOpen, setIsOpen] = useState(
    !! (! authorized || false === authenticated),
  );

  // Check whether the overlay can be closed when authentication/authorization
  // status changes.
  const closeModal = useCallback(() => {
    if (authorized && true === authenticated) {
      setIsOpen(false);
    }
  }, [authenticated, authorized]);

  let children;
  if (authenticated && authorized) {
    children = null;
  } else if (authenticated && ! authorized
    && isAuthCompleted) {
    const {
      provider,
      link,
      title,
    } = settings('strings.lock_screen.info_page');

    const outboundLink = 'link' === provider && link && title
      ? { link, title }
      : null;

    children = (
      <LockScreen
        actionButton={{
          onClick: logout,
          title: settings('strings.account.logout'),
        }}
        message={errorMessage || settings('strings.lock_screen.unauthorized')}
        outboundLink={outboundLink}
        style={overlayStyles}
      />
    );
  } else if (! authenticated && previewRemaining
      && isAuthCompleted) {
    const percent = previewRemaining ? previewRemaining / previewDuration : 0;
    const timestamp = secondsToTimestamp(previewRemaining);

    children = (
      <FreePreview
        percent={percent}
        timestamp={timestamp}
        onConnectClick={onConnectClick}
      />
    );
  } else if (! authenticated && ! previewRemaining
    && null !== previewRemaining && isAuthCompleted) {
    children = (
      <LockScreen
        actionButton={{
          onClick: onConnectClick,
          title: settings('strings.lock_screen.connect_button'),
        }}
        message={errorMessage || settings('strings.lock_screen.preview_ended')}
        style={overlayStyles}
      />
    );
  }

  /*
    This modifier turns the screen black until 'children' is loaded
    with the corresponding overlay.
  */
  const modifier = undefined === children ? '--loading' : '';

  return (
    <Modal
      ariaHideApp={false}
      bodyOpenClassName="has-anvato-player-overlay-open"
      className={`anvato-player-overlay__content${modifier}`}
      isOpen={isOpen}
      onRequestClose={closeModal}
      overlayClassName="anvato-player-overlay"
      parentSelector={playerMount}
      portalClassName="anvato-player-overlay-container"
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc={false}
      shouldFocusAfterRender={false}
    >
      {children}
    </Modal>
  );
};

PlayerOverlay.propTypes = {
  authenticated: PropTypes.bool.isRequired,
  authorized: PropTypes.bool.isRequired,
  isAuthCompleted: PropTypes.bool.isRequired,
  previewRemaining: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.oneOf([false]),
  ]).isRequired,
  onConnectClick: PropTypes.func.isRequired,
  errorMessage: PropTypes.string,
};

PlayerOverlay.defaultProps = {
  errorMessage: '',
};

export default PlayerOverlay;
